'use client'

import { ColumnDef } from '@tanstack/react-table'

import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { MoreHorizontal } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import useImpersonateB2BCustomer from '@/hooks/useImpersonateB2BCustomer'
import useImpersonateB2BPortal from '@/hooks/useImpersonateB2BPortal'
import { useDemoMode } from '@/context/DemoModeContext'
import { features } from '@/config/features'

// import { labels, priorities, statuses } from "../data/data";
import { DataTableColumnHeader } from '../../../components/shared/data-table/data-table-column-header'
import { on } from 'events'

//import { DataTableRowActions } from "./data-table-row-actions";

// account_code
// role
// price_listId
// price_list {
//   name
// }
// customer_groupId
// customer_group {
//   name
// }
// language
// is_b2b_user
// id
// name
// email
// b2b_company_address {
//   country
// }

export const getColumns = (
  onGroupClick: (group: any) => void,
  openAdvancedCustomerForm: any,
  onRestoreCustomer?: (customerId: number) => void
) => [
  {
    accessorKey: 'account_code',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Account Code" />
    ),
    cell: ({ row }) => {
      const { isDemoMode } = useDemoMode()
      return (
        <div
          onClick={() => openAdvancedCustomerForm(row.original)}
          className={`cursor-pointer hover:underline font-mono text-sm ${isDemoMode ? 'blur-sm select-none' : ''}`}
        >
          {row.getValue('account_code')}
        </div>
      )
    },
    enableSorting: true,
    enableHiding: true,
    meta: { width: '110px' },
  },
  {
    accessorKey: 'b2b_company_address.country',
    id: 'b2b_company_address.country',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Country" />
    ),
    enableSorting: true,
    enableHiding: true,
    meta: { width: '50px' },
  },
  {
    accessorKey: 'b2b_company_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Company Name" />
    ),
    enableSorting: true,
    enableHiding: true,
    cell: ({ row }) => {
      const { isDemoMode } = useDemoMode()
      return (
        <div
          className={`flex flex-wrap cursor-pointer hover:underline ${isDemoMode ? 'blur-sm select-none' : ''}`}
          onClick={() => openAdvancedCustomerForm(row.original)}
        >
          {row.getValue('b2b_company_name')}
        </div>
      )
    },
    enableResizing: true,
    meta: { width: '250px' },
  },
  {
    accessorKey: 'b2b_customer_discount',
    id: 'b2b_customer_discount',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Discount" />
    ),
    enableSorting: true,
    enableHiding: true,
    minSize: 100,
    size: 200,
    cell: ({ row }) => {
      return (
        <div className="flex min-w-[100px] ">
          {' '}
          {row.getValue('b2b_customer_discount') > 0 && (
            <Badge variant="secondary" className="text-xs cursor-pointer">
              {`${row.getValue('b2b_customer_discount')}%`}
            </Badge>
          )}
        </div>
      )
    },
  },
  {
    accessorKey: 'customer_group.name',
    id: 'customer_group.name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Group" />
    ),
    enableSorting: true,
    enableHiding: true,
    cell: ({ row }) => {
      const group = row.original.customer_group
      if (!group?.name) {
        return null
      }
      return (
        <Badge
          variant="secondary"
          className="text-xs cursor-pointer hover:underline"
          // onClick={() => onGroupClick(group)}
        >
          {group.name}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'price_list.name',
    id: 'price_list.name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Price List" />
    ),
    cell: ({ row }) => {
      if (!row.getValue('price_list.name')) {
        return null
      }
      return (
        <div className="flex min-w-[100px] ">
          {' '}
          <Badge variant="secondary" className="text-xs cursor-pointer">
            {row.getValue('price_list.name')}
          </Badge>
        </div>
      )
    },
    enableSorting: true,
    enableHiding: true,
    enableResizing: false,
    minSize: 200,
    size: 200,
    maxSize: 200,
    enableMultiSort: true,
  },
  {
    accessorKey: 'fabric_palettes',
    id: 'fabric_palettes',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Palettes" />
    ),
    cell: ({ row }) => {
      const palettes = row.original.fabric_palettes
      if (!palettes?.length) {
        return null
      }
      return (
        <div className="flex flex-wrap gap-1">
          {palettes.map((palette: any) => (
            <Badge key={palette.id} variant="outline" className="text-xs">
              {palette.name}
            </Badge>
          ))}
        </div>
      )
    },
    enableSorting: false,
    enableHiding: true,
    minSize: 100,
    size: 200,
  },
  {
    accessorKey: 'tags',
    id: 'tags',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Tags" />
    ),
    cell: ({ row }) => {
      const tags = row.original.tags
      if (!tags?.length) {
        return null
      }
      return (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag: any) => {
            const tagName = tag.tag_profiles?.find(
              (profile: any) => profile.language === 'en'
            )?.name
            if (!tagName) return null
            return (
              <Badge key={tag.id} variant="lightBlue" className="text-xs">
                {tagName}
              </Badge>
            )
          })}
        </div>
      )
    },
    enableSorting: false,
    enableHiding: true,
    minSize: 100,
    size: 200,
  },
  {
    accessorKey: 'additional_components',
    id: 'additional_components',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Components" />
    ),
    cell: ({ row }) => {
      const additionalComponents = row.original.additional_components
      if (!additionalComponents?.length) {
        return null
      }
      return (
        <div className="flex flex-wrap gap-1">
          {additionalComponents.map((component: any) => {
            // Try to get English name first, fallback to any available language
            const englishProfile =
              component.additionalComponent.additional_component_profiles?.find(
                (profile: any) => profile.language === 'en'
              )
            const anyProfile =
              component.additionalComponent.additional_component_profiles?.[0]
            const componentName =
              englishProfile?.name ||
              anyProfile?.name ||
              component.additionalComponent.code

            if (!componentName) return null
            return (
              <Badge key={component.id} variant="outline" className="text-xs">
                {componentName}
              </Badge>
            )
          })}
        </div>
      )
    },
    enableSorting: false,
    enableHiding: true,
    minSize: 100,
    size: 200,
  },
  {
    id: 'actions',
    header: () => null,
    cell: ({ row }) => {
      const { impersonate } = useImpersonateB2BCustomer()
      const { impersonatePortal } = useImpersonateB2BPortal()
      const isDeleted = !!row.original.deletedAt

      if (isDeleted && onRestoreCustomer) {
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRestoreCustomer(row.original.id)}
          >
            Restore
          </Button>
        )
      }

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {features.impersonation.storefront && (
              <DropdownMenuItem onClick={() => impersonate(row.original.id)}>
                Impersonate
              </DropdownMenuItem>
            )}
            {features.impersonation.b2bPortal && (
              <DropdownMenuItem
                onClick={() => impersonatePortal(row.original.id)}
              >
                Impersonate B2B Portal
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() =>
                // /dashboard/b2b-accounts/638/orders
                (window.location.href = `/dashboard/b2b-accounts/${row.original.id}/orders`)
              }
            >
              View Orders
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                // /dashboard/b2b-accounts/638/carts
                (window.location.href = `/dashboard/b2b-accounts/${row.original.id}/carts`)
              }
            >
              View Carts
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
    enableSorting: false,
    enableHiding: true,
    meta: { width: '50px' },
  },
]
